import React, { useContext, useEffect, useRef } from 'react';
import { AmplitudeTrackPayload } from '@kimuno/api-sdk/lib/types/requests/Track';
import { useSelector } from 'react-redux';
import {
  fetchAttachments as fetchAttachmentsAction,
  fetchProduct as fetchProductAction,
  fetchProductMindItems as fetchProductMindItemsAction,
} from '../../actions/product.actions';
import { CurrentProductContext } from '../../components/Product/CurrentProductContext';
import { NewProductContents } from '../../components/Product/ProductContents';
import { NewProductSummary } from '../../components/Product/ProductSummary';
import useAction from '../../utils/useAction';
import useRouter from '../../utils/useRouter';
import { setCustomPageTitle } from '../../utils/customTitle';
import { selectProduct } from '../../reducers/products.selectors';
import UpgradeBanner from '../../components/UpgradeBanner';
import { selectUser } from '../../reducers/user.selectors';
import { amplitudeContentTypes, amplitudeEvents, routes, zIndex } from '../../constants';
import { getContentEventPayload, getCurrentSearchId, waitForAmpliReady } from '../../utils/ampli';
import services from '../../services';
import { Product } from '@kimuno/api-sdk/lib/types/responses/Product';
import { User } from '../../types/User';
import { Box } from '@mui/material';
import { customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import { isEqual } from 'lodash';
import { GET_PRODUCT } from '../../actions/product.actionTypes';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { IconName } from '../../utils/getMuiIcon';
import { useTranslation } from 'react-i18next';
import { NotificationContext } from '../../state/NotificationContextProvider';
import NotificationAlert from '../../components/NotificationAlert';
import { Alert } from '../../components/NotificationAlert/NotificationAlert';
import getSearchPageUrl from '../../utils/getSearchPageUrl';
import { getPositionFromState, getPositionFromQuery } from '../../utils/searchUtils';
import { getPageSourceFromState, getPageSourceFromQuery, resolveDashboardSection } from '../../utils/pageSource';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import useHydrateSearchStateFromUrl from '../../utils/useHydrateSearchStateFromUrl';

const ProductPage = () => {
  const { match, router } = useRouter();
  const { params: { productId }, location: { query, state } } = match;
  const fetchAttachments = useAction(fetchAttachmentsAction);
  const fetchProduct = useAction(fetchProductAction);
  const fetchProductMindItems = useAction(fetchProductMindItemsAction);
  const product = useSelector(state => selectProduct(state, productId));
  const user = useSelector(selectUser);
  const { t } = useTranslation();
  const { notification, hideNotification, showNotification } = useContext(NotificationContext);

  useHydrateSearchStateFromUrl();

  const searchPageUrl = getSearchPageUrl(user);

  const handleShowNotification = (alert: Alert) => {
    showNotification(alert);
  };

  const trackContentViewed = async (
    product: Product | undefined,
    user: User | undefined,
  ) => {
    if (product && user) {
      await waitForAmpliReady();

      const searchId = getCurrentSearchId() || query?.search_id;
      const pageSource = getPageSourceFromState(state) ?? getPageSourceFromQuery(query);
      const dashboardSection = resolveDashboardSection(pageSource, state, query);

      const contentViewedPayload: AmplitudeTrackPayload | null = getContentEventPayload({
        content: product,
        event_type: amplitudeEvents.ContentViewed,
        contentType: amplitudeContentTypes.product,
        searchId,
        pageSource,
        dashboardSection,
      });

      if (contentViewedPayload) {
        const position = getPositionFromState(state) ?? getPositionFromQuery(query);

        // Include search id if there's an active search id (persists throughout search journey)
        if (searchId) {
          contentViewedPayload.event_properties = {
            ...contentViewedPayload.event_properties,
            ...(position !== undefined && { position }),
          } as typeof contentViewedPayload.event_properties;
        }
        services.track.ampliServerSideEventTrack(false, contentViewedPayload);
      }
    }
  };

  useEffect(() => {
    fetchProduct(productId)
      .then((result) => {
        if (!result) return;
        const { type } = result;
        if (type === GET_PRODUCT.ERROR) {
          router.push(`/${routes.dashboard.root}`);
        }
      });
    fetchProductMindItems(productId);
    fetchAttachments(productId);
  }, [productId]);

  const prevProductRef = useRef<Product | undefined>();
  const trackedProductIdRef = useRef<string>();

  useEffect(() => {
    if (!isEqual(prevProductRef.current, product)) {
      setCustomPageTitle(product?.title);
    }
    prevProductRef.current = product;

    if (product && user && trackedProductIdRef.current !== product.id) {
      trackedProductIdRef.current = product.id;
      trackContentViewed(product, user).catch(notifyBugsnagHandledError);
    }
  }, [product, user?.id]);

  const ProductPageBody = () => (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        margin: 'auto',
        padding: `${spaces.lg} ${customSizes.gridMargins} ${spaces.xxxl} ${customSizes.gridMargins}`,
        backgroundColor: palette.background.paperElevation2,
      }}>
        <NewProductSummary onShowNotification={handleShowNotification} />
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        margin: 'auto',
        padding: `${spaces.md} ${customSizes.gridMargins} ${spaces.xxxxxl} ${customSizes.gridMargins}`,
        backgroundColor: palette.background.paperElevation1,
      }}>
        <NewProductContents onShowNotification={handleShowNotification} />
      </Box>
    </>
  );

  const productPageContent = (
    <Box sx={{ minHeight: '100vh' }}>
      <UpgradeBanner />
      {notification && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.alert }}>
          <Box sx={{ position: 'relative' }}>
            <NotificationAlert
              message={notification.message}
              severity={notification.severity}
              onClose={hideNotification}
              alertSx={{
                width: '100%',
                marginLeft: `${spaces.md} !important`,
                marginRight: `${spaces.md} !important`,
              }}
            />
          </Box>
        </Box>
      )}
      <CurrentProductContext.Provider
        value={{
          productId
        }}
      >
        <ProductPageBody />
      </CurrentProductContext.Provider>
    </Box>
  );

  return (
    <MasterLayout
      customTopBarContent={<Breadcrumbs
        links={[
          {
            title: t('Search'),
            url: searchPageUrl,
            iconName: 'searchDone' as IconName,
          },
          {
            title: t('Product'),
            iconName: 'book' as IconName,
          },
        ]}
      />}
    >
      {productPageContent}
    </MasterLayout>
  );
};

export default ProductPage;
